// region Enums

/** ISO 639-1 language code, lowercase (e.g. "sk", "en"). */
export type LanguageCode = string;

export enum EntityTypeEnum {
    Page = "Page",
    Gallery = "Gallery",
    Post = "Post",
    Language = "Language"
}

export enum PostStateEnum {
    Draft = "Draft",
    Published = "Published",
}

export enum ToastSuccessMessage {
    dataSaved = "Dáta sa úspešne uložili",
    changesSaved = "Zmeny sa úspešne uložili",
    loggedIn = "Úspešne prihlásený",
    userCreated = "Úspešne zaregistrovaný",
    synced = "Úspešne synchronizované",
    rebuilt = "Web bol úspešne rebuildnutý",
}

export enum ToastFailureMessage {
    networkError = "Spojenie zlyhalo",
    emailAlreadyExists = "Používateľ s daným emailom už existuje",
    dataNotLoaded = "Dáta sa nepodarilo načítať",
    dataNotSaved = "Dáta sa nepodarilo uložiť",
    changesNotSaved = "Zmeny sa nepodarilo uložiť",
    changesNotSynced = "Zmeny sa nepodarilo synchronizovať",
    notRebuilt = "Web sa nepodarilo rebuildnúť",
    incorrectCredentials = "Nesprávne prihlasovacie údaje",
    accountBlocked = "Tento účet bol zablokovaný",
    error = "Nastala chyba",
    filesTooLarge = "Súbory presahujú maximálnu veľkosť 25 MB",
}

// endregion

// region Interfaces (Contexts)

export interface UserDataContext {
    id: number,
    displayName: string,
    email: string,
    isSuperAdmin: boolean,
    isPasswordTemporary: boolean,
    websites: Website[],
}

export type SiteDataContext = Website

// endregion

// region Interfaces (other)

export interface FrontendEntityChangeResponse {
    id: number;
    websiteId: number;
    entityType: EntityTypeEnum,
    changeAction: number
    insertedAt: string;
    insertDescription: string;
    insertedBy: UserResponse;
}

export interface FrontendEntityChangeListResponse {
    totalCount: number
    data: FrontendEntityChangeResponse[]
}

export interface PostGetRequest {
    id: number,
    websiteId: number
}

export type PostState = 'Draft' | 'Published';

export interface PostFilterRequest extends FilterBaseRequest {
    state?: PostState
}

export interface PostFilterResponse {
    totalCount: number,
    data: PostFilterData[]
}

export interface PostLanguage {
    languageShortcut: LanguageCode,
    isDefault: boolean
}

export interface PostFilterData {
    id?: number,
    headline: string,
    description: string,
    languages: PostLanguage[],
    link?: string,
    file?: FileItem,
    createdAt: string,
    createdById: number,
    modifiedAt?: string,
    modifiedById?: number,
    galleryName?: string,
    publishedAt?: string,
    state: PostState
}

export interface Post {
    id?: number,
    translations: PostTranslation[],
    link: string,
    gallery?: Gallery,
    galleryId?: number,
    file?: FileItem,
    fileId?: number,
    websiteId: number,
    createdAt: string,
    publishedAt?: string,
    state: PostState
}

export interface PostTranslation {
    headline: string,
    description: string,
    htmlText: string,
    languageShortcut: LanguageCode,
    postId: number,
    isDefault: boolean
}

export interface Translation {
    value: string,
    languageShortcut: LanguageCode,
    textId: number,
    isDefault: boolean
}

export interface Text {
    id: number,
    translations: Translation[]
}

export interface TextFilterData {
    id: number,
    code: string,
    translations: Translation[]
}

export interface TextFilterResponse {
    totalCount: number,
    data: TextFilterData[]
}

export interface ImageFile {
    id: number;
    file: File;         // For the actual file object during upload
    filePath: string;
    alt: string;
    createdAt: string;
    createdById: number;
    modifiedAt?: string;
    modifiedById?: number;
}

/** A concrete rendered use of an image (crop) as tracked by the backend ImageUsage table. */
export interface ImageUsage {
    /** Usage row id; 0 for a freshly picked image whose crop hasn't been saved yet. */
    id: number;
    imageId: number;
    filePath: string;
    alt: string;
}

export interface ImagesResponse {
    websiteId: number;
    totalCount: number;
    uploadedImages: ImageFile[];
}

export interface ChangeResponse {
    hasFrontendChanges: boolean;
}

export interface ImageUsageResponse {
    posts: UsageDto[];
    galleries: UsageDto[];
    titleImageGalleries: UsageDto[];
    pageImagePositions: UsageDto[];
    blockDelete: boolean;
}

export interface PageImagePositionResponse {
    imageUsage: ImageUsage;
    width: number;
    height: number;
}

export interface UpdatePageImagePositionRequest {
    positionId: number;
    imageId: number;
    coordinates: Coordinates;
}

export interface GalleryUsageResponse {
    posts: UsageDto[];
}

export interface UsageDto {
    id: number;
    name: string;
}

export interface ImageUploadRequest {
    websiteId: number;
    images: { [key: string]: ImageFile };
}

export interface List {
    id: number,
    code: string,
    listItemData: ListItem
}

export interface ListItem {
    icon: any, //TODO CHANGE TO IMAGE
    text: string,
    link?: string,
    listId: number
}

export interface ListUpdateRequest {
    listId: number,
    listItems: ListItem[]
}

export interface Category {
    id?: number,
    headline: string,
    htmlText: string,
    gallery?: Gallery,
    galleryId?: number,
    websiteId: number,
    coverImageId?: number,
    coverImageName?: string,
    imageWidth?: number,
    imageHeight?: number,
    coordinates?: Coordinates,
    minWidth?: number,
    minHeight?: number,
}

export interface CategoriesFilterResponse {
    totalCount: number,
    data: CategoriesFilterResponseData[],
}

export interface CategoriesFilterResponseData {
    id: number,
    headline: string,
    pageCode?: string,
    coverImageId?: number,
    coverImageName?: string,
    gallery: Gallery,
}

export interface ListFilterResponse {
    totalCount: number,
    data: List[],
}

export interface Section {
    id: number,
    code: string,
    websiteId: number,
    posts: Post[]
}

export interface SectionFilterResponse {
    totalCount: number,
    data: SectionFilterData[]
}

export interface SectionFilterData {
    id: number,
    code: string
}

export interface Coordinates {
    filled: boolean,
    left: number,
    up: number,
    right: number,
    down: number,
}

export interface TextResponse {
    id: number,
    pageId: number,
    translations: Translation[],
}

export interface ToastPayload {
    message: ToastSuccessMessage | ToastFailureMessage;
}

export interface UserFilterRequest {
    limit?: number,
    offset?: number,
    orderDirection?: OrderDirection,
}

export interface UserResponse {
    id: number
    displayName: string
    email: string
    isSuperAdmin: boolean
    isPasswordTemporary: boolean
    websites: Website[]
    lastPasswordChangeAt: string | Date
}

export interface UserFilterResponse {
    totalCount: number,
    data: UserResponse[],
}

export interface AdminUserNameItem {
    id: number,
    displayName: string,
}

export interface AdminUserNamesResponse {
    data: AdminUserNameItem[],
}

export type OrderDirection = 'Asc' | 'Desc';

export interface FilterBaseRequest {
    websiteId: number,
    limit?: number,
    offset?: number,
    orderDirection?: OrderDirection,
}

export interface TextFilterRequest extends FilterBaseRequest {
    pageId?: number
}

export interface SectionFilterRequest extends FilterBaseRequest {
    pageId: number
}

/** Stored visibility of a single gallery: listed on the albums page, or hidden from it. */
export type GalleryVisibility = 'Public' | 'Hidden';

/** Table filter selector — 'All' maps to both values before being sent to the backend. */
export type GalleryVisibilityFilter = GalleryVisibility | 'All';

export interface GalleryFilterRequest extends FilterBaseRequest {
    /** Omitted defaults to ['Public'] on the backend. */
    visibility?: GalleryVisibility[],
}

export interface PageFilterRequest extends FilterBaseRequest {

}

export interface TextUpdateRequest {
    TextId: number,
    Translations: Translation[]
}

export interface Gallery {
    id?: number,
    title: string,
    websiteId: number,
    titleImage: ImageUsage | null,
    galleryImages: ImageUsage[],
    /** Crop window in original-image pixels; kept client-side and sent on save. */
    coordinates?: Coordinates,
    visibility: GalleryVisibility
}

/** Payload shape of POST/PUT /Gallery (BaseGalleryRequest on the backend). */
export interface GallerySaveRequest {
    id?: number,
    title: string,
    websiteId: number,
    titleImageUsageId: number,
    titleImageUsage: { id: number, imageId: number, filePath: string },
    galleryImages: { id: number, filePath: string, alt: string }[],
    coordinates: Coordinates,
    visibility: GalleryVisibility,
}

export interface GalleriesFilterResponse {
    totalCount: number,
    data: GalleriesFilterResponseData[],
}

export interface PagesFilterResponse {
    totalCount: number,
    data: PageFilterResponseData[],
}

export interface PageFilterResponseData {
    id: number,
    code: string
}

export interface GalleriesFilterResponseData {
    id: number,
    title: string,
    titleImage: ImageUsage,
    galleryImages: ImageUsage[],
    visibility: GalleryVisibility,
    createdAt: string,
    createdById: number,
    modifiedAt?: string,
    modifiedById?: number,
}

export interface CatalogLanguage {
    code: LanguageCode,
    flagCountry?: string | null
}

export interface WebsiteLanguage {
    id: number,
    languageCode: LanguageCode,
    flagCountry?: string | null,
    isPublic: boolean,
    textTranslationCount: number,
    postTranslationCount: number
}

export interface WebsiteLanguageCreateRequest {
    websiteId: number,
    languageCode: LanguageCode,
    isPublic: boolean
}

export interface WebsiteLanguageUpdateRequest {
    id: number,
    isPublic: boolean
}

export interface WebsiteSettings {
    usesPost: boolean,
    usesGallery: boolean,
}

export interface Website {
    id: number
    domainName: string
    shortName: string
    settings: WebsiteSettings
}

export interface GetPagesForWebsiteResponse {
    pages: PagesForWebsiteData[]
}

export interface GetWebsitesResponse {
    data: WebsitesData[]
}

export interface WebsitesData {
    id: number,
    shortName: string,
    domainName: string
}

export interface PagesForWebsiteData {
    id: number,
    code: string,
    categoryCoverImageWidth: number,
    categoryCoverImageHeight: number
}

export interface AdminUserCreateRequest {
    displayName: string,
    email: string,
    password: string,
    isSuperAdmin: boolean,
    isPasswordTemporary: boolean,
    websiteIds: number[],
}

export interface AdminUserUpdateRequest {
    id: number,
    displayName: string,
    email: string,
    password: string,
    isSuperAdmin: boolean,
    websiteIds: number[],
    isPasswordTemporary: boolean,

}

export interface AdminSetPasswordRequest {
    id: number,
    password: string,
}

// endregion

export interface ListItemCreateRequest {
    listId: number,
    text: string,
    link?: string,
}

export interface ListItemResponse {
    id: number,
    listId: number,
    text: string,
    link?: string,
}

export interface UpdateSectionRequest {
    sectionId: number,
    postIds: number[],
}

export interface FileItem {
    id: number;
    fileName: string;
    filePath: string;
    websiteId: number;
    createdAt: string;
    createdById: number;
    modifiedAt?: string;
    modifiedById?: number;
}

export interface FilesResponse {
    websiteId: number;
    totalCount: number;
    files: FileItem[];
}

export interface FileUsageResponse {
    posts: UsageDto[];
}